// User types
export interface User {
  id: string;
  email: string;
  name: string;
  company?: string;
  role: string;
}

export interface Subscription {
  id: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete';
  planType: 'free' | 'monthly' | 'annual' | 'enterprise';
  currentPeriodEnd?: string;
  trialEnd?: string;
}

// Template types
export interface Template {
  id: string;
  name: string;
  isCustom: boolean;
  isSystem?: boolean;
  sections: Array<{
    title: string;
    prompt: string;
  }>;
}

// Page Layout types
export type PageLayout =
  | 'full-width'      // Text spans full width
  | 'image-right'     // Text left, image right
  | 'image-left'      // Image left, text right
  | 'image-header'    // Image top, text below
  | 'image-footer'    // Text top, image below
  | 'image-middle'    // Text above, visual centered, text below
  | 'two-column'      // Text in two columns
  | 'overlay';        // Text overlaid on image

export const PAGE_LAYOUTS: { id: PageLayout; name: string; description: string }[] = [
  { id: 'full-width', name: 'Full Width', description: 'Text spans the full page width' },
  { id: 'image-right', name: 'Image Right', description: 'Text on left, image on right' },
  { id: 'image-left', name: 'Image Left', description: 'Image on left, text on right' },
  { id: 'image-header', name: 'Image Header', description: 'Large image at top, text below' },
  { id: 'image-footer', name: 'Image Footer', description: 'Text at top, image below' },
  { id: 'image-middle', name: 'Middle Visual', description: 'Text above, visual element centered, text below' },
  { id: 'two-column', name: 'Two Column', description: 'Text flows in two columns' },
  { id: 'overlay', name: 'Overlay', description: 'Text overlaid on background image' },
];

// Image configuration for a section
export interface SectionImageConfig {
  enabled: boolean;
  provider: 'openai' | 'stability' | 'leonardo' | 'gemini';
  size: 'square' | 'portrait' | 'landscape';
  prompt?: string;  // Custom prompt (auto-generate from content if empty)
}

// Section types
export interface Section {
  id: number;
  title: string;
  prompt: string;
  content: string;
  isEditing: boolean;
  isGenerating: boolean;
  documents: Array<LocalFile | CloudFile>;
  role?: string;  // e.g. "introduction", "body", "conclusion", "transition"
  layout?: PageLayout;  // Page layout template
  imageConfig?: SectionImageConfig;
  isGeneratingImage?: boolean;
}

// Style matching options
export interface StyleOptions {
  copyTone: boolean;
  copyFormatting: boolean;
  copySentenceStructure: boolean;
  copyParagraphLength: boolean;
  copyVocabulary: boolean;
}

// Writing nuance settings
export interface NuanceSettings {
  formality: number;        // 1-5: casual → formal
  detailLevel: number;      // 1-5: concise → exhaustive
  perspective: 'first' | 'second' | 'third' | 'neutral';
  confidence: number;       // 1-5: hedging → assertive
  paragraphStyle: 'short' | 'medium' | 'long';
  transitions: 'minimal' | 'moderate' | 'heavy';
  repetitionHandling: 'allow' | 'reduce' | 'eliminate';
}

// Chain generation config
export interface ChainConfig {
  enabled: boolean;
  contextDepth: number;       // 1-100: how many prior sections to include
  contextFormat: 'full' | 'summary' | 'key-points';
  continueFlow: boolean;      // ensure each section flows from the previous
  // Story mode enhancements
  storyMode: boolean;         // Enable story-specific features
  generateOutline: boolean;   // Generate story outline before sections
  trackCharacters: boolean;   // Extract and track characters between sections
  creativityLevel: 'low' | 'medium' | 'high';  // Controls temperature
}

// Story outline generated before chain generation
export interface StoryOutline {
  premise: string;            // One-line story premise
  setting: string;            // Where/when the story takes place
  tone: string;               // Overall tone (humorous, dark, whimsical, etc.)
  themes: string[];           // Central themes
  plotBeats: PlotBeat[];      // Key story beats mapped to sections
  targetAudience?: string;    // Who the story is for
}

export interface PlotBeat {
  sectionIndex: number;
  sectionTitle: string;
  beatType: 'setup' | 'rising_action' | 'midpoint' | 'climax' | 'falling_action' | 'resolution';
  summary: string;            // What should happen in this section
  mustInclude?: string[];     // Key elements that must appear
}

// Story bible - maintained across sections for consistency
export interface StoryBible {
  characters: StoryCharacter[];
  locations: StoryLocation[];
  establishedFacts: string[];  // Things that have been stated as true
  plotProgress: string[];      // Key events that have happened
  openThreads: string[];       // Unresolved plot points to address
  lastSceneContext: string;    // Where/what was happening at end of last section
}

export interface StoryCharacter {
  name: string;
  description: string;        // Physical/personality description
  role: 'protagonist' | 'antagonist' | 'supporting' | 'minor';
  introducedIn: number;       // Section index where introduced
  traits: string[];           // Key personality traits
  relationships: { character: string; relationship: string }[];
}

export interface StoryLocation {
  name: string;
  description: string;
  firstAppearance: number;    // Section index
}

// Chain progress events (SSE)
export interface ChainProgressEvent {
  type: 'start' | 'planning' | 'planning_complete' | 'section_start' | 'section_complete' | 'bible_update' | 'section_error' | 'complete';
  sectionId?: number;
  sectionTitle?: string;
  content?: string;
  currentIndex?: number;
  totalSections?: number;
  error?: string;
  // Story mode additions
  outline?: StoryOutline;
  bible?: StoryBible;
  beatType?: string;          // Current section's position in story arc
}

// File types
export interface LocalFile {
  id: string;
  filename: string;
  filePath?: string;
  size?: number;
  content?: string;
}

export interface CloudFile {
  id: string;
  name: string;
  filename?: string;
  mimeType?: string;
  size?: number;
  modifiedTime?: string;
  source: 'local' | 'googleDrive' | 'dropbox' | 'oneDrive';
  filePath?: string;
  isFolder?: boolean;
}

// Generic field types
export type FieldType = 'text' | 'longText' | 'date' | 'select' | 'number' | 'email' | 'phone' | 'url';

export interface GenericField {
  id: string;
  label: string;
  value: string;
  type: FieldType;
  placeholder?: string;
  required?: boolean;
  options?: string[];
}

export interface GenericSection {
  id: string;
  title: string;
  fields: GenericField[];
  isExpanded: boolean;
  order: number;
}

// Document template
export interface DocumentTemplate {
  id: string;
  name: string;
  isCustom: boolean;
  sections: GenericSection[];
  createdAt?: string;
  updatedAt?: string;
}

// Applicant data
export interface ApplicantData {
  beneficiaryName: string;
  beneficiaryNationality: string;
  currentLocation: string;
  petitionerName: string;
  petitionerType: string;
  petitionerState: string;
  petitionerAddress: string;
  visaType: string;
  industry: string;
  complexity: string;
  priorityDate: string;
  filingDate: string;
  caseNumber: string;
  attorneyName: string;
  additionalInfo: string;
  llmProvider: string;
  llmModel: string;
  customFields: Array<{ id: number; label: string; value: string }>;
  genericFields?: Record<string, Record<string, string>>;
}

// Cloud connections
export interface CloudConnections {
  googleDrive: boolean;
  dropbox: boolean;
  oneDrive: boolean;
}

// Notification
export interface Notification {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

// Session metadata
export interface SessionMetadata {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  lastAccessedAt: string;
  documentType: string;
  preview: string;
  wordCount: number;
  sectionCount: number;
}

// Settings
export interface BrandingSettings {
  customLogoEnabled: boolean;
  logoPath?: string;
  companyName?: string;
  logoSize?: number;
  accentColor?: string;
  accentTextColor?: string;
}

export interface ChatSettings {
  enabled: boolean;
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  saveHistory: boolean;
  chatSystemPrompt?: string;
}

// AI Provider constants
export const AI_PROVIDERS: Record<string, { name: string; needsApiKey: boolean; needsBaseUrl: boolean; defaultBaseUrl?: string; placeholder?: string }> = {
  openai: { name: 'OpenAI', needsApiKey: true, needsBaseUrl: false, placeholder: 'e.g. gpt-4, gpt-4o, gpt-3.5-turbo' },
  anthropic: { name: 'Anthropic', needsApiKey: true, needsBaseUrl: false, placeholder: 'e.g. claude-3-5-sonnet-20241022' },
  gemini: { name: 'Google Gemini', needsApiKey: true, needsBaseUrl: false, placeholder: 'e.g. gemini-1.5-pro, gemini-1.5-flash' },
  openrouter: { name: 'OpenRouter', needsApiKey: true, needsBaseUrl: false, defaultBaseUrl: 'https://openrouter.ai/api/v1', placeholder: 'e.g. meta-llama/llama-3.1-70b-instruct' },
  ollama: { name: 'Ollama (Local)', needsApiKey: false, needsBaseUrl: true, defaultBaseUrl: 'http://localhost:11434/v1', placeholder: 'e.g. llama3, mistral, codellama' },
  stability: { name: 'Stability AI (Images)', needsApiKey: true, needsBaseUrl: false },
  leonardo: { name: 'Leonardo AI (Images)', needsApiKey: true, needsBaseUrl: false },
  custom: { name: 'Custom Endpoint', needsApiKey: true, needsBaseUrl: true, placeholder: 'e.g. your-model-id' },
};

export type AIProvider = string;

// Re-export image types
export * from './images';

// Re-export document configuration types
export * from './document';

// Re-export visualization types
export * from './visualizations';
